import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik, FormikProvider } from "formik";
import { Form } from "formik";
import ModalWrapper from "components/common/modals/modalWrapper/ModalWrapper";
import ConfirmDeleteModal from "components/Modal/ConfirmDeleteModal/ConfirmDeleteModal";
import {
  clearSelectedContent,
  setIsCopyMode,
} from "app/store/alertProtocol/slice/alertProtocolSlice";
import AlertProtocolForm from "./AlertProtocolForm";
import {
  useCreateAlertProtocolMutation,
  useUpdateAlertProtocolMutation,
} from "app/store/alertProtocol/api/alertProtocolApi";
import { showErrorToast, showSuccessToast } from "utils/toast";
import * as Yup from "yup";
import { isValidArray } from "utils/array";
import {
  NOTE_SECTION_VALUES,
  NOTE_SUB_SECTION_VALUES,
  UNIT_FILTER_NOTE_SECTIONS,
  UNIT_OPTIONS,
} from "static/alert_protocol";

// Validation Schema
const validationSchema = Yup.object().shape({
  account: Yup.string().required("Account is required"),
  deviceType: Yup.array()
    .of(Yup.string().required("Device type is required"))
    .min(1, "At least one device type is required")
    .required("Device type is required"),
  noteSection: Yup.string().required("Note section is required"),
  occurrence: Yup.string().required("Occurrence is required"),
  noteSubSection: Yup.string().when("noteSection", {
    is: "arrhythmia",
    then: (schema) => schema.required("Note subsection is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  oacStatus: Yup.string().when("noteSection", {
    is: "arrhythmia",
    then: (schema) => schema.required("OAC status is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  conditions: Yup.array()
    .of(
      Yup.object().shape({
        metric: Yup.string().required("Metric type is required"),
        operator: Yup.string().required("Operator is required"),
        value: Yup.array().min(1, "Value is required"),
        unit: Yup.string(),
      })
    )
    .min(1, "At least one metric is required"),
  alertType: Yup.string().required("Alert type is required"),
});

const AlertProtocolModal = () => {
  const dispatch = useDispatch();
  const {
    isEditModalOpen,
    selectedContent,
    isEditMode,
    isCopyMode,
    filterAccount,
    filterDeviceType,
  } = useSelector((state) => state.alertProtocol);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [metricToDelete, setMetricToDelete] = useState(null);
  const [fieldArrayHelpersRef, setFieldArrayHelpersRef] = useState(null);

  const [createAlertProtocol] = useCreateAlertProtocolMutation();
  const [updateAlertProtocol] = useUpdateAlertProtocolMutation();

  const formik = useFormik({
    initialValues: {
      account: filterAccount || "",
      deviceType: isValidArray(filterDeviceType)
        ? filterDeviceType
        : [filterDeviceType] || [],
      occurrence: selectedContent?.occurrence || "",
      noteSection: selectedContent?.note_section || "",
      noteSubSection: selectedContent?.sub_section || "",
      alertType: selectedContent?.alert_type || "Warning",
      oacStatus: selectedContent?.oac_status || "no",
      laaoStatus: selectedContent?.laao_status || false,
      conditions: selectedContent?.conditions || [],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => handleSubmit(values),
    enableReinitialize: true,
    validateOnChange: false,
  });

  const handleSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        conditions: values.conditions.map((condition) => {
          const newCondition = { ...condition };
          if (!newCondition.unit || newCondition.metric === "status") {
            delete newCondition.unit;
          }
          return newCondition;
        }),
        ...(isCopyMode && { isCopy: isCopyMode }),
      };

      if (
        values.noteSection !== NOTE_SECTION_VALUES.ARRHYTHMIA ||
        values.noteSubSection !== NOTE_SUB_SECTION_VALUES.AF
      ) {
        delete payload.oacStatus;
      }

      delete payload?._id;

      let response;

      if (isEditMode) {
        response = await updateAlertProtocol({
          id: selectedContent?._id,
          ...payload,
        }).unwrap();
      } else {
        response = await createAlertProtocol(payload).unwrap();
      }

      if (!response?.error) {
        showSuccessToast(response?.message);
        dispatch(clearSelectedContent());
        handleClose();
        formik.resetForm();
      }
    } catch (error) {
      showErrorToast(error?.message);
    }
  };

  const handleClose = () => {
    dispatch(clearSelectedContent());
    dispatch(setIsCopyMode(false));
    formik.resetForm();
    setMetricToDelete(null);
  };

  const handleDeleteMetric = (index, fieldArrayHelpers, conditions) => {
    if (conditions?.length === 1) {
      setMetricToDelete(index);
      setFieldArrayHelpersRef(fieldArrayHelpers);
      setDeleteConfirmOpen(true);
    } else {
      fieldArrayHelpers.remove(index);
    }
  };

  const handleConfirmDelete = () => {
    if (metricToDelete !== null && fieldArrayHelpersRef) {
      fieldArrayHelpersRef.remove(metricToDelete);
      setMetricToDelete(null);
      setFieldArrayHelpersRef(null);
    }
    setDeleteConfirmOpen(false);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setMetricToDelete(null);
  };

  const getUnitOptions = (metricIndex) => {
    let options = UNIT_OPTIONS;
    const { noteSection, conditions } = formik.values;

    if (
      conditions[metricIndex].metric === "duration" &&
      UNIT_FILTER_NOTE_SECTIONS.includes(noteSection)
    ) {
      options = options.filter((option) =>
        ["year", "month", "day", "hour", "minute"].includes(option.value)
      );
    }

    if (
      noteSection === "arrhythmia" &&
      conditions[metricIndex].metric === "rate"
    ) {
      options = options.filter((option) => option.value === "bpm");
    }

    return options;
  };

  return (
    <ModalWrapper
      title={
        isEditMode
          ? "Edit Alert Protocol"
          : `${isCopyMode ? "Copy" : "Add"} Alert Protocol`
      }
      show={isEditModalOpen}
      onClose={handleClose}
      maxWidth="md"
      actionButtons={[
        {
          label: "Cancel",
          variant: "outlined",
          onClick: handleClose,
        },
        {
          label: isEditMode
            ? "Update Alert Protocol"
            : `${isCopyMode ? "Copy" : "Add"} Alert Protocol`,
          variant: "contained",
          type: "submit",
          onClick: formik?.handleSubmit,
          disabled: formik?.isSubmitting,
        },
      ]}
    >
      <FormikProvider value={formik}>
        <Form onSubmit={formik.handleSubmit}>
          <AlertProtocolForm
            values={formik.values}
            errors={formik.errors}
            touched={formik.touched}
            setFieldValue={formik.setFieldValue}
            handleDeleteMetric={handleDeleteMetric}
            isCopyMode={isCopyMode}
            isEditMode={isEditMode}
            selectedContent={selectedContent}
            getUnitOptions={getUnitOptions}
          />
        </Form>
      </FormikProvider>

      <ConfirmDeleteModal
        isModalOpen={deleteConfirmOpen}
        onClose={handleCancelDelete}
        onDelete={handleConfirmDelete}
        isLoading={false}
        modalTitleText="Delete Metric"
        maxWidth="xs"
        modalBodyText={
          <p style={{ textAlign: "left" }}>
            Are you sure you want to remove this metric? <br />A minimum of one
            metric is required to create an alert protocol.
          </p>
        }
      />
    </ModalWrapper>
  );
};

export default AlertProtocolModal;
